import { ArrowRight, Bell, CircleAlert, Clock3 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { wave, waveQueries } from '@round/api';
import CampaignArtwork from '@/creator/components/CampaignArtwork';
import BudgetUsedMeter from '@/creator/components/BudgetUsedMeter';
import PlatformIcon from '@/creator/components/PlatformIcon';
import { getTimeLeft, PLATFORM_ICON, PLATFORM_LABEL } from '@/utility/campaignDisplay';
import { getInviteIdByCampaignId } from '@/utility/creatorCampaigns';
import { formatMoney } from '@/utility/money';
import { handleTabKeyDown } from '@/utility/tabKeyboard';
import '@/creator-app.css';

/**
 * Reworked logged-in creator home (ROU-1192), ported from the prototype's
 * HomePage. The greeting name is wired to me/.first_name; the invite alert and
 * active-campaign cards are wired to the real me/invites and me/campaigns reads,
 * and the period earnings tabs to the server-converted wallet totals. The headline
 * answers "what have I earned", so lifetime includes pending accruals and the
 * settled/pending split sits beneath it (ROU-1504).
 */

// The wallet totals field each earnings tab reads; all three are pre-converted
// into the creator's display currency server-side.
type EarningsPeriodId = 'lifetime' | 'month' | 'week';

type EarningsPeriod = {
    id: EarningsPeriodId;
    label: string;
    field: keyof Pick<wave.WalletConvertedTotals, 'lifetime' | 'last_30_days' | 'last_7_days'>;
};

const EARNINGS_PERIODS: EarningsPeriod[] = [
    { id: 'lifetime', label: 'Lifetime', field: 'lifetime' },
    { id: 'month', label: '30 days', field: 'last_30_days' },
    { id: 'week', label: '7 days', field: 'last_7_days' },
];

const EARNINGS_PERIOD_IDS = EARNINGS_PERIODS.map((period) => period.id);

const isUnanswered = (invite: wave.CreatorInvite): boolean => {
    return invite.status === 'invited' && invite.response === null;
};

type HomeCampaignCardProps = {
    campaign: wave.CreatorCampaign;
    onOpen: () => void;
};

const HomeCampaignCard = ({ campaign, onOpen }: HomeCampaignCardProps) => {
    return (
        <article className="campaign-card">
            <CampaignArtwork src={campaign.artwork_url} alt={`${campaign.release_name} artwork`} size="featured" />
            <div className="campaign-card__content">
                <div className="campaign-card__top">
                    <h2>{campaign.release_name}</h2>
                </div>
                <div className="campaign-card__meta">
                    <span>
                        <Clock3 size={14} /> <span>{getTimeLeft(campaign.deadline)}</span>
                    </span>
                    <span
                        className="campaign-card__accounts"
                        aria-label={`${PLATFORM_LABEL[campaign.platform]}, ${campaign.artist_name}`}
                    >
                        <span className="campaign-card__platforms" aria-hidden="true">
                            <span
                                className={`platform-logo platform-logo--${PLATFORM_ICON[
                                    campaign.platform
                                ].toLowerCase()}`}
                            >
                                <PlatformIcon platform={PLATFORM_ICON[campaign.platform]} size={11} />
                            </span>
                        </span>
                        {campaign.artist_name}
                    </span>
                </div>
                <BudgetUsedMeter percentage={campaign.budget_used_percentage} isCompact />
                <div className="campaign-card__footer">
                    <button className="button button--primary campaign-card__action" type="button" onClick={onOpen}>
                        <span>View campaign</span> <ArrowRight size={15} />
                    </button>
                </div>
            </div>
        </article>
    );
};

const HomePage = () => {
    const navigate = useNavigate();
    const [earningsPeriod, setEarningsPeriod] = useState<EarningsPeriodId>('lifetime');

    const selfQuery = useQuery(waveQueries.creatorSelf.options.self());
    const invitesQuery = useQuery(waveQueries.invite.options.mine());
    const campaignsQuery = useQuery(waveQueries.creatorCampaign.options.mine());
    const walletQuery = useQuery(waveQueries.wallet.options.balances());

    const firstName = selfQuery.data?.first_name?.trim() ?? '';
    const greetingName = firstName === '' ? null : firstName;

    const totals = walletQuery.data?.totals ?? null;
    const selectedPeriod = EARNINGS_PERIODS.find((period) => period.id === earningsPeriod) ?? EARNINGS_PERIODS[0];
    // display_currency is null until the creator picks one; totals then fall back to GBP.
    const earningsSymbol = totals?.display_currency?.symbol ?? '£';
    const earningsValue = formatMoney(earningsSymbol, totals?.[selectedPeriod.field] ?? 0);
    // Every total counts pending accruals, so the lifetime headline carries a
    // settled/pending split. The period tabs have no such split to show: the server
    // returns one windowed figure, not its two halves.
    const pendingTotal = Number(totals?.pending ?? 0);
    const pendingLabel = formatMoney(earningsSymbol, totals?.pending ?? 0);
    const settledLabel = formatMoney(earningsSymbol, totals?.settled ?? 0);
    const earningsSplit =
        earningsPeriod === 'lifetime' && pendingTotal > 0 ? `${settledLabel} settled · ${pendingLabel} pending` : null;

    const unansweredInvitationCount = (invitesQuery.data ?? []).filter(isUnanswered).length;
    const inviteIdByCampaignId = useMemo(() => getInviteIdByCampaignId(invitesQuery.data ?? []), [invitesQuery.data]);
    const activeCampaigns = (campaignsQuery.data ?? []).filter((campaign) => campaign.is_open);

    const handleViewCampaigns = (): void => navigate('/campaigns');
    const handleViewCampaign = (inviteId: string): void => navigate(`/campaigns/${inviteId}`);

    return (
        <div className="page home-page">
            <header className="page-heading">
                <div>
                    <h1>{greetingName ? `Hi 👋 ${greetingName}` : 'Hi 👋'}</h1>
                </div>
            </header>

            <section className="home-earnings" aria-label="Earnings summary">
                <div className="home-earnings__heading">
                    <h2>Earnings</h2>
                    <div className="home-earnings__periods" role="tablist" aria-label="Earnings period">
                        {EARNINGS_PERIODS.map((period) => {
                            const isActive = earningsPeriod === period.id;
                            return (
                                <button
                                    key={period.id}
                                    type="button"
                                    role="tab"
                                    id={`home-earnings-${period.id}-tab`}
                                    aria-selected={isActive}
                                    aria-controls="home-earnings-panel"
                                    tabIndex={isActive ? 0 : -1}
                                    className={isActive ? 'is-active' : ''}
                                    onClick={() => setEarningsPeriod(period.id)}
                                    onKeyDown={(event) =>
                                        handleTabKeyDown(event, EARNINGS_PERIOD_IDS, earningsPeriod, setEarningsPeriod)
                                    }
                                >
                                    {period.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="home-earnings__body">
                    <div
                        id="home-earnings-panel"
                        className="home-earnings__value"
                        role="tabpanel"
                        aria-labelledby={`home-earnings-${earningsPeriod}-tab`}
                    >
                        {walletQuery.isError ? (
                            <span className="home-earnings__message">
                                Couldn’t load your earnings. Please refresh the page.
                            </span>
                        ) : walletQuery.isLoading ? (
                            <span className="home-earnings__message">Loading…</span>
                        ) : (
                            <>
                                <strong key={`${earningsSymbol}-${earningsPeriod}`}>{earningsValue}</strong>
                                {earningsSplit !== null && (
                                    <small className="home-earnings__split">{earningsSplit}</small>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </section>

            {invitesQuery.isError ? (
                <section className="home-invite-alert" aria-label="Campaign invitations">
                    <span className="home-invite-alert__icon" aria-hidden="true">
                        <CircleAlert size={17} />
                    </span>
                    <span className="home-invite-alert__copy">
                        <strong>Couldn’t load your invitations</strong>
                        <small>Please refresh the page.</small>
                    </span>
                    <button type="button" onClick={() => void invitesQuery.refetch()}>
                        <span>Retry</span> <ArrowRight size={14} />
                    </button>
                </section>
            ) : unansweredInvitationCount > 0 ? (
                <section className="home-invite-alert" aria-label="New campaign invitations">
                    <span className="home-invite-alert__icon" aria-hidden="true">
                        <Bell size={17} />
                    </span>
                    <span className="home-invite-alert__copy">
                        <strong>
                            {`${unansweredInvitationCount} new campaign invitation${
                                unansweredInvitationCount === 1 ? '' : 's'
                            }`}
                        </strong>
                        <small>Waiting for your response</small>
                    </span>
                    <button type="button" onClick={handleViewCampaigns}>
                        <span>Review</span> <ArrowRight size={14} />
                    </button>
                </section>
            ) : null}

            <div className="home-section-heading">
                <h2>Active campaigns</h2>
            </div>

            {campaignsQuery.isError ? (
                <section className="home-invite-alert" aria-label="Active campaigns">
                    <span className="home-invite-alert__icon" aria-hidden="true">
                        <CircleAlert size={17} />
                    </span>
                    <span className="home-invite-alert__copy">
                        <strong>Couldn’t load your campaigns</strong>
                        <small>Please refresh the page.</small>
                    </span>
                    <button type="button" onClick={() => void campaignsQuery.refetch()}>
                        <span>Retry</span> <ArrowRight size={14} />
                    </button>
                </section>
            ) : (
                <div className="home-campaign-list">
                    {activeCampaigns.map((campaign) => {
                        const inviteId = inviteIdByCampaignId.get(campaign.id);
                        if (!inviteId) return null;
                        return (
                            <HomeCampaignCard
                                key={campaign.id}
                                campaign={campaign}
                                onOpen={() => handleViewCampaign(inviteId)}
                            />
                        );
                    })}
                </div>
            )}

            <div className="home-link-row">
                <span>Looking for invitations or past campaigns?</span>
                <button className="text-button" type="button" onClick={handleViewCampaigns}>
                    <span>View campaigns</span> <ArrowRight size={14} />
                </button>
            </div>
        </div>
    );
};

export default HomePage;
